import React from 'react';
import styled from 'styled-components';
import { IconButton, withStyles } from '@material-ui/core';
import { InsertComment } from '@material-ui/icons';
import { white, green, red } from '@avangard/ui/colors';

import { useCurrentUser } from '@modules/user/hooks';
import { useChats } from '@modules/chat/hooks';
import { ChatsDrawer } from '@modules/chat/organisms';
import { ChatType } from '@modules/types/graphql';
import { useChatsManager } from './context';

const POLL_INTERVAL = 15_000;

const insertCommentIcon = <InsertComment style={{ fontSize: 24 }} />;

const ChatsManager = () => {
    const { currentUser } = useCurrentUser();

    const { filters } = useChatsManager();

    const [openChatsDrawer, setOpenChatsDrawer] = React.useState(false);

    const handleOpenChatsDrawer = () => setOpenChatsDrawer(true);
    const handleCloseChatsDrawer = () => setOpenChatsDrawer(false);

    const skipChats = React.useMemo(() => {
        if (!filters) {
            return true;
        }

        let skipByUserRole = !currentUser.hasAdminPrivileges() && !currentUser.isSchool();

        if (filters.type === ChatType.documentManagment) {
            return skipByUserRole && !currentUser.isDocumentModerator();
        }

        return skipByUserRole;
    }, [currentUser, filters]);

    const { chats, startPolling, stopPolling } = useChats({
        skip: skipChats,
        variables: {
            type: filters?.type,
            entityId: filters?.entityId,
            ownerId: filters?.ownerId,
        },
    });

    const showChats = !!filters;
    const resultChats = chats.slice(0, 1);

    React.useEffect(() => {
        if (showChats) {
            startPolling(POLL_INTERVAL);
        } else {
            stopPolling();
        }

        return () => {
            stopPolling();
        };
    }, [showChats]);

    if (!showChats || resultChats.length === 0) {
        return null;
    }

    // Пока не добавили фильтрацию по чатам - считаем что всегда один чат
    const newMessages = resultChats[0].getNewMessagesCount();

    return (
        <Root>
            <StyledIconButton disableRipple onClick={handleOpenChatsDrawer}>
                {insertCommentIcon}

                {newMessages > 0 ? <NewMessagesCounter>{newMessages}</NewMessagesCounter> : null}
            </StyledIconButton>

            <ChatsDrawer
                open={openChatsDrawer}
                chats={resultChats}
                onClose={handleCloseChatsDrawer}
            />
        </Root>
    );
};

const StyledIconButton = withStyles(() => ({
    root: {
        padding: 14,
        background: red[100],
        boxShadow: '0 5px 8px rgba(0, 0, 0, .2)',
        transition: 'all .2s',

        '&:hover': {
            background: red[100],
            boxShadow: '0 5px 8px rgba(0, 0, 0, .1)',
        },
    },

    label: {
        color: white[100],
    },
}))(IconButton);

const Root = styled.div`
    position: fixed;
    right: 52px;
    bottom: 40px;
    z-index: 50;
`;

const NewMessagesCounter = styled.span`
    position: absolute;
    top: -5px;
    right: -5px;

    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;

    width: 20px;
    height: 20px;

    color: ${white[100]};
    font-size: 1.2rem;
    font-weight: 500;

    background-color: ${green[100]};
    border-radius: 50%;
`;

export { ChatsManager };
